import { compact, get, values } from "lodash";

import { ColumnType, ITableData, IWrokspaceData } from "../types/table-type";
import {
  ContentSortType,
  StatisticsType,
  IFormComponentType,
  IChartFormValue,
  CalculationType,
  TimeCycleType,
  AggregateFunctions,
  IFormBlockOption,
  IAggregateFormValue,
  IValueSelectOption,
  IFormValue,
} from "../types/form-type";
import { AppType, ChartType, IAppConfig } from "../types/global-type";

import {
  getDimensionOptions,
  getDimensionDefaultValue,
  statisticsTypeOption,
  calculationTypeOption,
  timeCycleOption,
  getYAxisDefaultValue,
  getYAxisOptions,
  checkValueInArr,
  contentSortTypeOption,
  getAggregateOptions,
  checkShowSplitSwitch,
  checkShowSortSelect,
  getCalculationTypeOption,
} from "./form_secondary";
import { convertValueStringToObject } from "./app";
import { getDefaultFilterValue } from "./filter_main";
import { getRealColumn, showUseOptionColorSwitch } from "./option_main";
import { sortColumnsAndRows } from "./tableData_main";
import { isDateType } from "./tableData_secondary";

import { IS_USE_AXIS_CHART, IS_PIE_CHART, IS_LINE_CHART } from "../constants/chart";

export const getFormDefaultValue = (tableData: ITableData, appType: AppType): IFormValue => {
  let defaultValue: IFormValue;
  if (appType === AppType.CHART) {
    defaultValue = calculationChartFormDefaultValue(tableData);
  } else {
    defaultValue = getAggregateDefaultFormValue(tableData);
  }
  return defaultValue;
};

/**
 * 计算表单默认值 chart
 */
const calculationChartFormDefaultValue = (tableData: ITableData): IChartFormValue => {
  return {
    chartType: ChartType.BASIC_BAR,
    dimensionId: getDimensionDefaultValue(tableData),
    calculationTargetId: getYAxisDefaultValue(tableData),
    isGroupAtCycle: false,
    isSplit: true,
    isShowDimensionNull: false,
    statisticsType: StatisticsType.COUNT,
    calculationType: CalculationType.SUM,
    contentSort: ContentSortType.X_FIRST_TO_LAST,
    isShowLegend: true,
    isShowYAxis: true,
    isShowLabel: false,
    groupAtCycle: TimeCycleType.DAY,
    moreFold: false,
    filterFold: true,
    filterMeta: getDefaultFilterValue(tableData.columns),
  };
};

// 计算表单默认值 aggregate
const getAggregateDefaultFormValue = (tableData: ITableData): IFormValue => {
  const calcColumnOptions = getAggregateOptions(tableData);
  const calculationTargetId = calcColumnOptions.length > 0 ? calcColumnOptions[0].value : "";
  const calcTypeOptions = getCalculationTypeOption(
    { statisticsType: StatisticsType.STATISTIC_COLUMN, calculationTargetId } as IAggregateFormValue,
    tableData
  );
  const calculationType = calcTypeOptions.length > 0 ? calcTypeOptions[0].value : AggregateFunctions.EMPTY;
  const result = {
    statisticsType: StatisticsType.COUNT,
    calculationTargetId,
    calculationType,
    filterFold: true,
    filterMeta: getDefaultFilterValue(tableData.columns),
  };
  return result;
};

/**
 * 获取 chart 表单 config 用于渲染 该 config 不包含 chartTypeSelect
 * @param tableDat
 */
export const getChartFormConfig = (
  tableData: ITableData,
  formValue: IChartFormValue,
  chartType: ChartType,
  shareUserId: string
): IFormBlockOption[] => {
  const result = [
    // =========================== chart type ===========================
    {
      componentType: IFormComponentType.FormBlock,
      id: "formTitleChartType",
      labelId: "form.title.chart.type",
      allowFolding: false,
      childrenOption: [
        {
          id: "chartType",
          componentType: IFormComponentType.ChartTypeSelect,
        },
      ],
    },
    // =========================== chart type ===========================

    // =========================== x 轴配置 ===========================
    {
      componentType: IFormComponentType.FormBlock,
      id: "formTitleAxiosX",
      labelId: IS_PIE_CHART[chartType] ? "form.title.axios.dimension" : "form.title.axios.x",
      allowFolding: false,
      childrenOption: [
        // 维度 选择
        {
          id: "dimensionId",
          componentType: IFormComponentType.SelectForTree,
          options: getDimensionOptions(tableData),
          validates: [
            {
              checkFunc: ({ value, options }: { value: string; options: IValueSelectOption[] }) => {
                return !!options.find((item) => item.value === value);
              },
              errorMessageId: "error.form.select.value.invalidation",
            },
          ],
        },
        // 是否按周期合并
        {
          labelId: "form.label.isGroupAtCycle",
          id: "isGroupAtCycle",
          componentType: IFormComponentType.Switch,
          isShow: (value: IChartFormValue): boolean => {
            const columnType = getRealColumn(value.dimensionId, tableData)?.type;
            return isDateType(columnType);
          },
        },
        // 按周期合并
        {
          id: "groupAtCycle",
          componentType: IFormComponentType.SelectForTab,
          options: timeCycleOption,
          isShow: (value: IChartFormValue): boolean => {
            const columnType = getRealColumn(value.dimensionId, tableData)?.type;
            return isDateType(columnType) && (value.isGroupAtCycle || false);
          },
        },
        // 是否统计维度空值
        {
          labelId: IS_PIE_CHART[chartType] ? "form.label.isShowDimensionNull2" : "form.label.isShowDimensionNull",
          id: "isShowDimensionNull",
          componentType: IFormComponentType.Switch,
          isShow: (value: IChartFormValue): boolean => {
            const columnType = getRealColumn(value.dimensionId, tableData)?.type;
            if (!columnType) return true;
            if (columnType === ColumnType.CHECKBOX) return false;
            return true;
          },
        },
        // 是否拆分计算
        checkShowSplitSwitch(formValue, tableData) && {
          labelId: "form.label.isSplit",
          id: "isSplit",
          componentType: IFormComponentType.Switch,
        },
      ],
    },
    // =========================== x 轴配置 ===========================

    // =========================== y 轴配置 ===========================
    {
      componentType: IFormComponentType.FormBlock,
      id: "formTitleAxiosY",
      labelId: IS_PIE_CHART[chartType] ? "form.title.axios.value" : "form.title.axios.y",
      allowFolding: false,
      childrenOption: compact([
        // value 计算方式
        {
          labelId: "form.label.statisticsType",
          id: "statisticsType",
          componentType: IFormComponentType.SelectForTab,
          options: statisticsTypeOption,
        },
      ]),
    },
    {
      componentType: IFormComponentType.FormBlock,
      id: "formTitleAxiosYOther",
      allowFolding: false,
      childrenOption: compact([
        // 计算 value 所用 column
        {
          labelId: "",
          id: "calculationTargetId",
          isShow: (value: IChartFormValue) => value.statisticsType === StatisticsType.STATISTIC_COLUMN,
          componentType: IFormComponentType.SelectForTree,
          options: getYAxisOptions(tableData),
          isFormattedMessage: false,
          offset: [32, -175],
          validates: [
            {
              checkFunc: ({ value, options }: { value: string; options: IValueSelectOption[] }) => {
                if (!value) return true;
                return !!options.find((item) => item.value === value);
              },
              errorMessageId: "error.form.select.value.invalidation",
            },
          ],
        },
        // value 计算函数
        {
          id: "calculationType",
          isShow: (value: IChartFormValue) => value.statisticsType === StatisticsType.STATISTIC_COLUMN,
          componentType: IFormComponentType.Select,
          options: calculationTypeOption,
          isFormattedMessage: true,
        },
      ]),
    },
    // =========================== y 轴配置 ===========================

    // =========================== 图表堆积 ===========================
    // 分组
    IS_USE_AXIS_CHART[chartType] && {
      componentType: IFormComponentType.FormBlock,
      id: "stackFold",
      labelId: checkValueInArr(chartType, [ChartType.STACKED_COLUMN_BAR]) ? "form.title.stack" : "form.title.group",
      // allowFolding: true,
      childrenOption: compact([
        {
          labelId: checkValueInArr(chartType, [ChartType.STACKED_COLUMN_BAR])
            ? "form.label.stackByColumnId"
            : "form.label.groupByColumnId",
          id: "stackByColumnId",
          componentType: IFormComponentType.SelectForTree,
          options: getDimensionOptions(tableData),
          cancelSelection: true,
          validates: [
            {
              checkFunc: ({ value, options }: { value: string; options: IValueSelectOption[] }) => {
                if (!value) return true;
                return !!options.find((item) => item.value === value);
              },
              errorMessageId: "error.form.select.value.invalidation",
            },
          ],
          showClearIcon: true,
        },
      ]),
    },
    // =========================== 图表堆积 ===========================

    // =========================== 更多设置 ===========================
    {
      componentType: IFormComponentType.FormBlock,
      id: "moreFold",
      labelId: "form.title.more",
      allowFolding: true,
      childrenOption: compact([
        // 图表显示排序
        checkShowSortSelect(formValue, tableData) && {
          labelId: "form.label.contentSort",
          id: "contentSort",
          componentType: IFormComponentType.Select,
          options: contentSortTypeOption,
          isFormattedMessage: true,
        },
        // 显示图例
        {
          labelId: "form.label.isShowLegend",
          id: "isShowLegend",
          componentType: IFormComponentType.Switch,
          isShow: (value: IChartFormValue): boolean => {
            if (checkValueInArr(chartType, [ChartType.STACKED_COLUMN_BAR])) {
              return true;
            }
            if (checkValueInArr(chartType, [ChartType.BASIC_BAR, ChartType.BASIC_LINE])) {
              return !!value.stackByColumnId;
            }
            return true;
          },
        },
        // 使用 option 的颜色
        {
          labelId: "form.label.useOptionColor",
          id: "useOptionColor",
          componentType: IFormComponentType.Switch,
          isShow: (value: IChartFormValue): boolean => {
            return showUseOptionColorSwitch({ formValue: value, tableData });
          },
        },
        // 是否显示 y轴坐标 隐藏内容：y轴轴线  y轴label 基于y轴刻度和x轴平行的线（暂定）
        !IS_PIE_CHART[chartType]
          ? {
              labelId: "form.label.isShowYAxis",
              id: "isShowYAxis",
              componentType: IFormComponentType.Switch,
            }
          : null,
        // 显示数值标签
        {
          labelId: "form.label.isShowLabel",
          id: "isShowLabel",
          componentType: IFormComponentType.Switch,
        },
        // 开启平滑曲线
        IS_LINE_CHART[chartType] && {
          labelId: "form.label.isSmoothLine",
          id: "isSmoothLine",
          componentType: IFormComponentType.Switch,
        },
      ]),
    },
    // =========================== 更多设置 ===========================

    // =========================== 筛选设置 ===========================
    {
      componentType: IFormComponentType.FormBlock,
      id: "filterFold",
      labelId: "form.title.filter",
      allowFolding: true,
      childrenOption: [
        {
          id: "filterMeta",
          componentType: IFormComponentType.Filter,
          options: tableData.columns,
          shareUserId,
        },
      ],
    },
    // =========================== 筛选设置 ===========================
  ];
  // @ts-ignore
  return compact(result);
};

export const getAggregateFormConfig = (tableData: ITableData): IFormBlockOption[] => {
  const result = [
    // =========================== y 轴配置 ===========================
    {
      componentType: IFormComponentType.FormBlock,
      id: "formTitleAxiosY",
      labelId: "form.title.aggregate",
      allowFolding: false,
      childrenOption: compact([
        // value 计算方式
        {
          labelId: "form.label.statisticsType",
          id: "statisticsType",
          componentType: IFormComponentType.SelectForTab,
          options: statisticsTypeOption,
        },
        // 计算 value 所用 column
        {
          labelId: "",
          id: "calculationTargetId",
          isShow: (value: IFormValue<AppType.AGGREGATE>) => value.statisticsType === StatisticsType.STATISTIC_COLUMN,
          componentType: IFormComponentType.SelectForTree,
          options: getAggregateOptions(tableData),
          isFormattedMessage: false,
          offset: [32, -175],
          validates: [
            {
              checkFunc: ({ value, options }: { value: string; options: IValueSelectOption[] }) => {
                if (!value) return true;
                return !!options.find((item) => item.value === value);
              },
              errorMessageId: "error.form.select.value.invalidation",
            },
          ],
        },
        // value 计算函数
        {
          id: "calculationType",
          isShow: (value: IFormValue<AppType.AGGREGATE>) => value.statisticsType === StatisticsType.STATISTIC_COLUMN,
          componentType: IFormComponentType.Select,
          getOptions: (formValue: IFormValue, tableData: ITableData) =>
            getCalculationTypeOption(formValue as IAggregateFormValue, tableData),
          isFormattedMessage: true,
        },
      ]),
    },

    {
      componentType: IFormComponentType.FormBlock,
      id: "filterFold",
      labelId: "form.title.filter",
      allowFolding: true,
      childrenOption: [
        {
          id: "filterMeta",
          componentType: IFormComponentType.Filter,
          options: tableData.columns,
        },
      ],
    },
  ];

  // @ts-ignore
  return result;
};

export const parsingAppConfig = (data: any): IAppConfig | null => {
  const configStr = get(data, "getAppConfig.body.config");
  if (!configStr) return null;
  const config = convertValueStringToObject(configStr);
  return config;
};

export const convertAllData = (list: any[]) => {
  const [workspaceData, tableData, appConfig] = list;
  const result = {
    workspaceData: workspaceData ? (get(workspaceData, "workspace") as IWrokspaceData) : undefined,
    tableData: tableData ? (get(tableData, "getTableData") as ITableData) : undefined,
    appConfig: parsingAppConfig(appConfig) || undefined,
  } as {
    workspaceData?: IWrokspaceData;
    tableData?: ITableData;
    appConfig?: IAppConfig;
  };
  if (!!result.tableData) {
    sortColumnsAndRows(result.tableData);
  }
  return result;
};
